import { getRuntimeConfig } from './runtimeConfig';

/**
 * Shared utility for resolving API URLs with sessionStorage override support
 * Consolidates the API URL resolution logic used across multiple components
 */

/**
 * Get the current API URL including sessionStorage overrides
 * This function checks for sessionStorage overrides first (from configuration UI)
 * then falls back to environment configuration
 */
export function getCurrentApiUrl(): string {
  // Check for sessionStorage override first (from MosApiConfiguration )
  const overrideUrl = sessionStorage.getItem(
    'config_override_VITE_MOS_API_URL',
  );
  if (overrideUrl) {
    return overrideUrl;
  }

  // Fallback to environment configuration
  try {
    const config = getRuntimeConfig();
    return (config.VITE_MOS_API_URL as string) ?? '';
  } catch {
    return '';
  }
}

/**
 * Check if API URL is configured (either via override or environment)
 */
export function isApiUrlConfigured(): boolean {
  const apiUrl = getCurrentApiUrl();
  return Boolean(apiUrl && apiUrl.trim() !== '');
}

/**
 * Get API URL with validation
 * Throws an error if API URL is not configured
 */
export function getValidatedApiUrl(): string {
  const apiUrl = getCurrentApiUrl();
  if (!apiUrl || apiUrl.trim() === '') {
    throw new Error('API URL not configured');
  }
  return apiUrl;
}

/**
 * Create event listeners for API URL changes
 * Returns cleanup function to remove listeners
 */
export function createApiUrlChangeListeners(
  onApiUrlChange: () => void,
): () => void {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'config_override_VITE_MOS_API_URL') {
      onApiUrlChange();
    }
  };

  const handleConfigChange = () => {
    onApiUrlChange();
  };

  globalThis.addEventListener('storage', handleStorageChange);
  globalThis.addEventListener('mos-api-config-changed', handleConfigChange);

  // Return cleanup function
  return () => {
    globalThis.removeEventListener('storage', handleStorageChange);
    globalThis.removeEventListener(
      'mos-api-config-changed',
      handleConfigChange,
    );
  };
}
